/**
 * Local Print Agent client.
 * Connects the React app to the Node.js Local Print Agent at http://127.0.0.1:9100.
 * Use GET /health, GET /printers, GET /printers/default, and POST /print.
 */

const BASE_URL =
	(typeof process !== 'undefined' &&
		process.env?.REACT_APP_LOCAL_PRINT_AGENT_URL) ||
	'http://127.0.0.1:9100';

export type PrintPayload =
	| { type: 'text'; data: string; printer?: string; timeoutSeconds?: number; timeoutMs?: number }
	| { type: 'raw'; data: string; printer?: string; timeoutSeconds?: number; timeoutMs?: number }
	| { type: 'pdf'; data: string; printer?: string; timeoutSeconds?: number; timeoutMs?: number };

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
	const res = await fetch(`${BASE_URL}${path}`, {
		...init,
		headers: { 'Content-Type': 'application/json', ...init?.headers },
	});
	if (!res.ok) {
		const t = await res.text();
		throw new Error(
			`Local Print Agent ${path}: ${res.status} ${res.statusText}${
				t ? ` — ${t}` : ''
			}`,
		);
	}
	return res.json();
}

/** GET /health → { status, service } */
export async function getHealth(): Promise<{
	status: string;
	service?: string;
}> {
	return fetchJson<{ status: string; service?: string }>('/health');
}

/** GET /printers → array of printer names or { printers: string[] } */
export async function getPrinters(): Promise<string[]> {
	const data = await fetchJson<{ printers?: string[] } | string[]>('/printers');
	if (Array.isArray(data)) return data;
	return (data as { printers: string[] }).printers ?? [];
}

/** GET /printers/default → { name: string } or similar */
export async function getDefaultPrinter(): Promise<string | null> {
	try {
		const data = await fetchJson<{ name?: string; default?: string }>(
			'/printers/default',
		);
		return (data as any).name ?? (data as any).default ?? null;
	} catch {
		return null;
	}
}

/**
 * POST /print — send a print job.
 * Payload: { printer?, type, data, timeoutSeconds?, timeoutMs? }
 * - type 'raw': agent uses timeoutSeconds/timeoutMs for raw print (default 15s if not set).
 */
export async function print(
	payload: PrintPayload,
): Promise<{ ok?: boolean; message?: string }> {
	const body: Record<string, unknown> = {
		printer: payload.printer,
		type: payload.type,
		data: payload.data,
	};
	if (payload.timeoutSeconds != null) body.timeoutSeconds = payload.timeoutSeconds;
	if (payload.timeoutMs != null) body.timeoutMs = payload.timeoutMs;
	return fetchJson<{ ok?: boolean; message?: string }>('/print', {
		method: 'POST',
		body: JSON.stringify(body),
	});
}

/**
 * Send plain text to the local print agent.
 * Uses default printer if printerName is omitted.
 */
export async function printText(
	text: string,
	printerName?: string,
): Promise<void> {
	await print({ type: 'text', data: text, printer: printerName ?? undefined });
}

/**
 * Send raw ESC-POS bytes (base64-encoded) to the local print agent.
 * Use this for receipt formatting (left/center/right alignment, bold, cut, etc.).
 * Uses default printer if printerName is omitted.
 * Optional timeoutSeconds (e.g. 30) overrides agent default (15s) to avoid "Raw print timeout (15s)".
 */
export async function printRaw(
	base64EscPos: string,
	printerName?: string,
	opts?: { timeoutSeconds?: number; timeoutMs?: number },
): Promise<void> {
	await print({
		type: 'raw',
		data: base64EscPos,
		printer: printerName ?? undefined,
		...(opts?.timeoutSeconds != null && { timeoutSeconds: opts.timeoutSeconds }),
		...(opts?.timeoutMs != null && { timeoutMs: opts.timeoutMs }),
	});
}
